import { memo, useState } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
// GitHub Dark theme for code block syntax highlighting
import 'highlight.js/styles/github-dark.css'
import { resolveApiBase } from '../../lib/mountBase'

interface MarkdownViewerProps {
  content: string
  appName?: string  // For resolving relative image URLs via file API
  currentPath?: string  // Directory of the markdown file for relative paths
}

const LARGE_FILE_THRESHOLD = 200 * 1024 // 200KB

function MarkdownViewerInner({ content, appName, currentPath }: MarkdownViewerProps) {
  const [renderLarge, setRenderLarge] = useState(false)
  const isLarge = content.length > LARGE_FILE_THRESHOLD
  const apiBase = resolveApiBase(typeof window !== 'undefined' ? window.location.pathname : '/')

  // For large files, show raw text with a toggle
  if (isLarge && !renderLarge) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-3 p-2 rounded bg-yellow-500/10 border border-yellow-500/20 text-sm">
          <span className="text-yellow-600 dark:text-yellow-400">
            Large file ({(content.length / 1024).toFixed(0)} KB) — showing raw text for performance.
          </span>
          <button
            onClick={() => setRenderLarge(true)}
            className="px-2 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-xs font-medium"
          >
            Render as Markdown
          </button>
        </div>
        <pre className="text-xs font-mono bg-black text-green-400 p-3 rounded overflow-auto whitespace-pre-wrap">
          {content}
        </pre>
      </div>
    )
  }

  // Custom components for react-markdown
  const components = {
    // Resolve relative image paths through the file API
    img: ({ src, alt, ...props }: React.ImgHTMLAttributes<HTMLImageElement>) => {
      let resolvedSrc = src || ''
      // If it's a relative path and we have an app name, construct API URL
      if (appName && src && !src.startsWith('http://') && !src.startsWith('https://') && !src.startsWith('data:')) {
        // Build path relative to the markdown file's directory
        const dir = currentPath ? currentPath.split('/').slice(0, -1).join('/') : ''
        const imagePath = src.startsWith('/') ? src : (dir ? `${dir}/${src}` : src)
        resolvedSrc = `${apiBase}/deployed-apps/${encodeURIComponent(appName)}/files/content?path=${encodeURIComponent(imagePath)}`
      }
      return (
        <img
          src={resolvedSrc}
          alt={alt || ''}
          className="max-w-full rounded"
          loading="lazy"
          {...props}
        />
      )
    },
  }

  return (
    <div className="prose prose-sm dark:prose-invert max-w-none prose-pre:bg-[#0d1117] prose-pre:border prose-pre:border-border prose-code:before:content-none prose-code:after:content-none">
      <Markdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={components}
      >
        {content}
      </Markdown>
    </div>
  )
}

// Memoize to prevent re-renders when parent state changes (file browser navigation)
const MarkdownViewer = memo(MarkdownViewerInner)
export default MarkdownViewer
